import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import type {
  MutationEnablePluginArgs,
  MutationStartBoardExportArgs,
  MutationUpdateBoardOrgArgs,
  MutationUpdateBoardVisibilityArgs,
  MutationUpdateBoardCardCoversPrefArgs,
  MutationUpdateBoardSwitcherViewsPrefArgs,
  MutationUpdateCalendarKeyArgs,
  MutationUpdateCalendarFeedEnabledPrefArgs,
  MutationRemoveMemberFromBoardArgs,
  MutationReopenBoardArgs,
  MutationDeleteBoardArgs,
  MutationUpdateHiddenPluginBoardButtonShortcutsArgs,
  MutationCreateBoardInviteSecretArgs,
  MutationDisableBoardInviteSecretArgs,
  MutationUpdateBoardMemberPermissionArgs,
  MutationAddMemberToBoardArgs,
  QueryBoardsSearchArgs,
  BoardExportArgs,
  BoardStatsArgs,
  BoardDashboardViewTileArgs,
  MutationUpdateBoardNameArgs,
  MutationAddBoardArgs,
} from '../generated';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { token } from '@trello/session-cookie';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { trelloFetch, fetch } from '@trello/fetch';
import { getNetworkClient } from '../getNetworkClient';
import type { TrelloRestResolver } from '../types';
import { Analytics } from '@trello/atlassian-analytics';
import Cookies from 'js-cookie';
import { isQueryInfo } from '../isQueryInfo';

export const addBoard: TrelloRestResolver<MutationAddBoardArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const { traceId, ...body } = args;
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl('/1/boards');
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
      'X-Trello-TraceId': traceId,
    },
    body: JSON.stringify({
      ...body,
      token: context.token || token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(traceId, trelloServerVersion);

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const board = await response.json();

  return prepareDataForApolloCache(board, rootNode);
};

export const enablePlugin: TrelloRestResolver<MutationEnablePluginArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}/boardPlugins`);
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
      'X-Trello-TraceId': args.traceId,
    },
    body: JSON.stringify({
      idPlugin: args.pluginId,
      token: context.token || token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(args.traceId, trelloServerVersion);

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const updateBoardOrg: TrelloRestResolver<MutationUpdateBoardOrgArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': context.clientAwareness.version,
  };

  if (args.traceId) {
    headers['X-Trello-TraceId'] = args.traceId;
  }

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}`);
  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      keepBillableGuests: args.keepBillableGuests,
      idOrganization: args.orgId,
      token: context.token || token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(args.traceId, trelloServerVersion);

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const updateBoardVisibility: TrelloRestResolver<MutationUpdateBoardVisibilityArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      keepBillableGuests: args.keepBillableGuests,
      'prefs/permissionLevel': args.visibility,
      idOrganization: args.orgId,
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const updateBoardCardCoversPref: TrelloRestResolver<MutationUpdateBoardCardCoversPrefArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.boardId}`),
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        'prefs/cardCovers': args.cardCovers,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const updateBoardSwitcherViewsPref: TrelloRestResolver<MutationUpdateBoardSwitcherViewsPrefArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.boardId}`),
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        'prefs/switcherViews': args.switcherViews,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const boardExportResolver: TrelloRestResolver<BoardExportArgs> = async (
  board: {
    id: string; // idBoard
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/boards/${board.id}/exports/${args.id}`,
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Board.export',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, rootNode, 'Board') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const startBoardExport: TrelloRestResolver<MutationStartBoardExportArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.id}/exports`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
        attachments: true,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const statsResolver: TrelloRestResolver<BoardStatsArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await trelloFetch(
    networkClient.getUrl(`/1/boards/${args.id}/stats`),
    { credentials: 'same-origin' },
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Board.stats',
        operationName: context.operationName,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    } else {
      const error = await response.text();
      console.error(error);
      throw new Error(error);
    }
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, rootNode, 'Board');
};

export const boardDashboardViewTileResolver: TrelloRestResolver<BoardDashboardViewTileArgs> = async (
  board: {
    id: string; // idBoard
  },
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  let model = null;

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/boards/${board.id}/dashboardViewTiles/${args.id}`,
  );

  try {
    const response = await trelloFetch(apiUrl, undefined, {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Board.DashboardViewTile',
        operationName: context.operationName,
      },
    });

    if (response.ok) {
      model = await response.json();
    } else {
      if (response.status === 404) {
        model = null;
      } else {
        throw new Error(
          `An error occurred while resolving a GraphQL query. (status: ${response.status}, statusText: ${response.statusText})`,
        );
      }
    }

    return model ? prepareDataForApolloCache(model, rootNode, 'Board') : model;
  } catch (err) {
    console.error(err);
    return model;
  }
};

export const updateCalendarKey: TrelloRestResolver<MutationUpdateCalendarKeyArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.boardId}/calendarKey/generate`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const key = await response.json();

  return prepareDataForApolloCache(key, rootNode);
};

export const updateCalendarFeedEnabledPref: TrelloRestResolver<MutationUpdateCalendarFeedEnabledPrefArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.boardId}`),
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        'prefs/calendarFeedEnabled': args.calendarFeedEnabled,
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const removeMemberFromBoard: TrelloRestResolver<MutationRemoveMemberFromBoardArgs> = async (
  obj,
  { idBoard, idMember, traceId },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  try {
    const response = await fetch(`/1/Board/${idBoard}/members/${idMember}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
        'X-Trello-TraceId': traceId ?? '',
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    });

    const trelloServerVersion = response.headers.get('X-Trello-Version');
    Analytics.setTrelloServerVersion(traceId, trelloServerVersion);

    return prepareDataForApolloCache({ success: true }, rootNode);
  } catch (err) {
    return prepareDataForApolloCache(
      {
        success: false,
        error: (err as Error).message,
      },
      rootNode,
    );
  }
};

export const reopenBoard: TrelloRestResolver<MutationReopenBoardArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      closed: false,
      token: context.token || token,
      keepBillableGuests: args.keepBillableGuests,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const closeBoard: TrelloRestResolver<MutationReopenBoardArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      closed: true,
      token: context.token || token,
      keepBillableGuests: args.keepBillableGuests,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const deleteBoard: TrelloRestResolver<MutationDeleteBoardArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(`/1/boards/${args.boardId}`),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
        'X-Trello-TraceId': args.traceId,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(args.traceId, trelloServerVersion);

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  await response.json();

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const updateHiddenPluginBoardButtonShortcuts: TrelloRestResolver<MutationUpdateHiddenPluginBoardButtonShortcutsArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const url = `/1/boards/${args.boardId}/prefs/hiddenPluginBoardButtons/?value=${args.pluginId}`;
  const method = 'PUT';

  const response = await fetch(url, {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }

  const key = await response.json();

  return prepareDataForApolloCache(key, rootNode);
};

export const boardInviteSecretRequestResolver: TrelloRestResolver<MutationCreateBoardInviteSecretArgs> = async (
  _parent,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const url = `/1/boards/${args.idBoard}/invitationSecret`;
  const response = await trelloFetch(
    url,
    { credentials: 'same-origin' },
    {
      clientVersion: context.clientAwareness.version,
      networkRequestEventAttributes: {
        source: 'graphql',
        resolver: 'Board.invite',
        operationName: context.operationName,
      },
    },
  );

  if (!response.ok) {
    return prepareDataForApolloCache({}, rootNode);
  }

  const key = await response.json();

  return prepareDataForApolloCache(key, rootNode);
};

export const createBoardInviteSecret: TrelloRestResolver<MutationCreateBoardInviteSecretArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const url = `/1/boards/${args.idBoard}/invitationSecret`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
      type: args.type,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const key = await response.json();

  return prepareDataForApolloCache(key, rootNode);
};

export const disableBoardInviteSecret: TrelloRestResolver<MutationDisableBoardInviteSecretArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const url = `/1/boards/${args.idBoard}/invitationSecret`;
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  await response.json();

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const updateBoardMemberPermission: TrelloRestResolver<MutationUpdateBoardMemberPermissionArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const url = `/1/boards/${args.boardId}/members/${args.memberId}`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
      'X-Trello-TraceId': args.traceId,
    },
    body: JSON.stringify({
      type: args.type,
      token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(args.traceId, trelloServerVersion);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error);
  }
};

export const addMemberToBoard: TrelloRestResolver<MutationAddMemberToBoardArgs> = async (
  obj,
  {
    boardId,
    member,
    type = 'normal',
    allowBillableGuest = false,
    invitationMessage,
    acceptUnconfirmed = false,
    traceId = '',
  },
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const { id: memberId, email: memberEmail } = member;
  if (!memberId && !memberEmail) {
    throw new Error();
  }

  const urlParams = new URLSearchParams({ type: type! });
  memberEmail && urlParams.append('email', memberEmail);

  const trailingPath =
    (memberId ? `/${memberId}` : '') + `?${urlParams.toString()}`;

  const additionalHeaders: HeadersInit = {};
  if (traceId) {
    additionalHeaders['X-Trello-TraceId'] = traceId;
  }

  const response = await fetch(
    networkClient.getUrl(`/1/boards/${boardId}/members${trailingPath}`),
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
        ...additionalHeaders,
      },
      body: JSON.stringify({
        token: context.token || token,
        invitationMessage,
        allowBillableGuest,
        acceptUnconfirmed,
      }),
    },
  );

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  await response.json();

  return prepareDataForApolloCache({ success: true }, rootNode);
};

export const boardsSearchResolver: TrelloRestResolver<QueryBoardsSearchArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/search`);

  const params = new URLSearchParams();
  params.set('board_fields', 'all');
  params.set('query', args.query);
  params.set('boards_limit', args.limit.toString());
  params.set('modelTypes', 'boards');
  params.set('partial', (!args.query.endsWith(' ')).toString());
  const dscCookie = Cookies.get('dsc');
  if (dscCookie) {
    params.set('dsc', dscCookie);
  }

  const response = await trelloFetch(`${apiUrl}?${params}`, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'boardsSearch',
      operationName: context.operationName,
    },
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }
  return prepareDataForApolloCache((await response.json()).boards, rootNode);
};

export const updateBoardName: TrelloRestResolver<MutationUpdateBoardNameArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];

  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(`/1/boards/${args.boardId}`);
  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
      'X-Trello-TraceId': args.traceId,
    },
    body: JSON.stringify({
      name: args.boardName,
      token: context.token || token,
    }),
  });

  const trelloServerVersion = response.headers.get('X-Trello-Version');
  Analytics.setTrelloServerVersion(args.traceId, trelloServerVersion);

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });
    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};
